import { Combobox, Transition } from '@headlessui/react'
import { SearchIcon } from '@heroicons/react/outline'
import { useState, useEffect, ChangeEvent, Fragment } from 'react'
import SmartyStreetsSDK, { core } from 'smartystreets-javascript-sdk'

import { Address } from '@models/address'
import styles from './Combobox.module.css'
import { formatAddress } from '@utils/format-address'

interface AddressSearchProps {
  address?: Address
  onSelect: (address: Address) => void
  onSelectMissing: () => void
  suggestMissing?: boolean
  clearAddress: () => void
  setTouched?: () => void
  name?: string
  isErrored?: boolean
  isLoading?: boolean
}

// No need for custom motion components - using Headless UI Transition

export function AddressSearch(props: AddressSearchProps) {
  const [query, setQuery] = useState(formatAddress(props.address))
  const [suggestions, setSuggestions] = useState<
    SmartyStreetsSDK.usAutocompletePro.Suggestion[]
  >([])
  const [smartystreets] = useState(() => {
    const credentials = new core.SharedCredentials(
      process.env.NEXT_PUBLIC_SMARTYSTREETS_API_KEY ?? ''
    )

    return core.buildClient.usAutocompletePro(credentials)
  })

  function handleQueryChange(event: ChangeEvent<HTMLInputElement>) {
    props.clearAddress()
    setQuery(event.currentTarget.value)
  }

  useEffect(() => {
    if (props.address) {
      setQuery(formatAddress(props.address))
    }
  }, [props.address])

  useEffect(() => {
    if (query.trim() !== '') {
      const lookup = new SmartyStreetsSDK.usAutocompletePro.Lookup(query)

      smartystreets
        .send(lookup)
        .then(({ result }) => {
          setSuggestions(result ?? [])
        })
        .catch(() => {})
    }
  }, [query])

  function handleSelect(option: string) {
    if (option === 'MISSING_ADDRESS') {
      props.onSelectMissing()
      return
    }

    if (option.trim()) {
      const [street, city, stateAndZip] = option.split(', ')
      const [state, zipCode] = stateAndZip.split(' ')
      const address: Address = {
        street,
        city,
        state,
        zipCode
      }
      const formattedAddress = formatAddress(address)

      setQuery(formattedAddress)
      props.onSelect(address)
    }
  }

  return (
    <Combobox value={query} onChange={handleSelect}>
      {({ open }) => (
        <div className="relative">
          <div className="flex focus-within:ring focus-within:ring-blue-100 rounded">
            <div
              className={`flex justify-center items-center px-4 rounded-l border border-r-0 ${
                props.isErrored ? styles.errorButton : styles.button
              }`}
            >
              {props.isLoading ? (
                <svg
                  className="animate-spin h-6 w-6 text-brand"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                <SearchIcon width={24} height={24} />
              )}
            </div>
            <Combobox.Input
              id="addressSearch"
              name={props.name}
              displayValue={() => query}
              placeholder="Search for an address..."
              onBlur={props.setTouched}
              onChange={handleQueryChange}
              className={`w-full p-4 rounded-r border border-l-0 focus:outline-none ${
                props.isErrored ? styles.errorInput : styles.input
              }`}
            />
          </div>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Combobox.Options
              className={`${styles.popover} absolute z-50 w-full mt-1`}
            >
              <div className="divide-y divide-gray-100 rounded-md">
                {suggestions.map(
                  ({ streetLine, secondary, city, state, zipcode }) => {
                    const address = `${streetLine}, ${city}, ${state} ${zipcode}`
                    const key = `${streetLine}-${secondary}-${city}-${state}-${zipcode}`

                    return (
                      <Combobox.Option
                        key={key}
                        value={address}
                        className={({ active }) =>
                          `${styles.option} ${active ? 'bg-gray-100' : ''}`
                        }
                      >
                        {address}
                      </Combobox.Option>
                    )
                  }
                )}
                {suggestions.length < 10 && (
                  <Combobox.Option
                    value="MISSING_ADDRESS"
                    className={({ active }) =>
                      `${styles.missingOption} ${active ? 'bg-gray-100' : ''}`
                    }
                  >
                    Can't find your address?
                  </Combobox.Option>
                )}
              </div>
            </Combobox.Options>
          </Transition>
        </div>
      )}
    </Combobox>
  )
}
